/**
 * Seeded Random Number Generator
 *
 * Provides deterministic pseudo-random numbers based on a seed.
 * Used for platform variations - same platform ID always gets same variation.
 *
 * Using a simple LCG (Linear Congruential Generator) implementation.
 */

export class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed % 2147483647;
    if (this.seed <= 0) this.seed += 2147483646;
  }

  /**
   * Get next random number between 0 and 1
   */
  next(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return (this.seed - 1) / 2147483646;
  }

  /**
   * Get random integer between min (inclusive) and max (exclusive)
   */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min)) + min;
  }

  /**
   * Get random float between min and max
   */
  nextFloat(min: number, max: number): number {
    return this.next() * (max - min) + min;
  }

  /**
   * Get random boolean with given probability
   */
  nextBoolean(probability: number = 0.5): boolean {
    return this.next() < probability;
  }

  /**
   * Pick random element from array
   */
  pick<T>(array: T[]): T {
    if (array.length === 0) {
      throw new Error('Cannot pick from empty array');
    }
    return array[this.nextInt(0, array.length)]!;
  }

  /**
   * Shuffle array (Fisher-Yates)
   */
  shuffle<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = this.nextInt(0, i + 1);
      const temp = result[i];
      const swapVal = result[j];
      if (temp !== undefined && swapVal !== undefined) {
        result[i] = swapVal;
        result[j] = temp;
      }
    }
    return result;
  }
}

/**
 * Generate consistent seed from platform ID and additional identifiers
 */
export function generatePlatformSeed(platformId: number, zoneId: number = 1): number {
  // Use simple hash combination
  return (platformId * 7919 + zoneId * 6421) % 2147483647;
}
